import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Check, Loader2, ArrowRight } from 'lucide-react'
import { api } from '@/lib/api'
import { useToast } from '@/hooks/use-toast'

type TierSlug = 'associate' | 'partner' | 'principal'
type Interval = 'monthly' | 'annual'

interface TierDef {
  slug: TierSlug
  name: string
  monthly: number
  annual: number
  promise: string
  highlights: string[]
}

const TIERS: TierDef[] = [
  {
    slug: 'associate',
    name: 'Associate',
    monthly: 397,
    annual: 3970,
    promise: 'A full-service AI intake layer for your website.',
    highlights: [
      'Tailored AI chat + knowledge base',
      'Bookings, live staff takeover',
      'Team mgmt, chat history, alerts',
      'Analytics dashboard',
    ],
  },
  {
    slug: 'partner',
    name: 'Partner',
    monthly: 897,
    annual: 8970,
    promise: 'Intake that never sleeps — web and phone.',
    highlights: [
      'Everything in Associate',
      'AI answers every call, 24/7',
      'Dedicated number, 250 calls/mo',
      'Transcripts, summaries, caller history',
    ],
  },
  {
    slug: 'principal',
    name: 'Principal',
    monthly: 1497,
    annual: 14970,
    promise: 'Every inquiry becomes a conversation.',
    highlights: [
      'Everything in Partner',
      'Proactive callback on web inquiries',
      'CRM sync + reminder calls',
      '600 calls/mo, priority support',
    ],
  },
]

export interface ChangePlanModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  currentTier: TierSlug | string
  currentInterval: Interval
  onPlanChanged: () => void
  // Preselect a tier when the modal opens — used by the plans page so the
  // user doesn't have to pick the tier twice (once on the cards, once in
  // the modal). The modal still lets them change their mind.
  initialSelectedTier?: TierSlug
}

interface ChangePreview {
  immediateChargeCents: number
  nextInvoiceTotalCents: number
  currency: string
  currentPeriodEnd: number
  tier: TierSlug
  interval: Interval
}

function formatMoney(cents: number, currency = 'usd'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(cents / 100)
}

export function ChangePlanModal({
  open,
  onOpenChange,
  currentTier,
  currentInterval,
  onPlanChanged,
  initialSelectedTier,
}: ChangePlanModalProps) {
  const [selectedTier, setSelectedTier] = useState<TierSlug | null>(null)
  const [interval, setInterval] = useState<Interval>(currentInterval)
  const [preview, setPreview] = useState<ChangePreview | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const { toast } = useToast()

  // Reset when modal opens/closes. If a preselected tier was passed in (and
  // it isn't the user's current tier), skip the picker step and go straight
  // to the proration preview.
  useEffect(() => {
    if (open) {
      const preselect =
        initialSelectedTier && initialSelectedTier !== currentTier
          ? initialSelectedTier
          : null
      setSelectedTier(preselect)
      setInterval(currentInterval)
      setPreview(null)
      setPreviewError(null)
    }
  }, [open, currentInterval, currentTier, initialSelectedTier])

  // Fetch proration preview whenever tier or interval changes
  useEffect(() => {
    if (!selectedTier || !open) {
      setPreview(null)
      return
    }
    // Skip if user picked their current plan
    if (selectedTier === currentTier && interval === currentInterval) {
      setPreview(null)
      setPreviewError('This is your current plan.')
      return
    }

    let cancelled = false
    setPreviewLoading(true)
    setPreviewError(null)

    api
      .get('/api/billing/subscription/change-preview', {
        params: { tier: selectedTier, interval },
      })
      .then((res) => {
        if (cancelled) return
        setPreview(res.data.data)
      })
      .catch((err) => {
        if (cancelled) return
        setPreviewError(err.response?.data?.message ?? err.message ?? 'Could not load preview')
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedTier, interval, open, currentTier, currentInterval])

  const handleConfirm = async () => {
    if (!selectedTier) return
    setSubmitting(true)
    try {
      await api.post('/api/billing/subscription/change', {
        tier: selectedTier,
        interval,
      })
      toast({
        title: 'Plan changed',
        description: `You're now on the ${TIERS.find((t) => t.slug === selectedTier)?.name} plan.`,
      })
      onPlanChanged()
      onOpenChange(false)
    } catch (err: any) {
      toast({
        title: 'Plan change failed',
        description: err.response?.data?.message ?? err.message ?? 'Please try again',
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading">Change your plan</DialogTitle>
          <DialogDescription>
            Proration is calculated automatically. You'll see the exact charge before you confirm.
          </DialogDescription>
        </DialogHeader>

        {/* Interval toggle */}
        <div className="flex items-center gap-2 border-b pb-3">
          <button
            type="button"
            onClick={() => setInterval('monthly')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              interval === 'monthly' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval('annual')}
            className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              interval === 'annual' ? 'bg-accent text-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Annual
            <span className="ml-1.5 inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider">2 mo free</span>
          </button>
        </div>

        {/* Tier cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {TIERS.map((tier) => {
            const isCurrent = tier.slug === currentTier && interval === currentInterval
            const isSelected = selectedTier === tier.slug
            return (
              <button
                key={tier.slug}
                type="button"
                onClick={() => !isCurrent && setSelectedTier(tier.slug)}
                disabled={isCurrent}
                className={`text-left rounded-xl border p-4 transition-colors ${
                  isCurrent
                    ? 'opacity-60 cursor-not-allowed bg-muted/30'
                    : isSelected
                      ? 'border-brand-royal bg-brand-royal/5 ring-1 ring-brand-royal'
                      : 'hover:border-brand-royal/40 hover:bg-accent/30'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="text-sm font-semibold">{tier.name}</h3>
                  {isCurrent && (
                    <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                      Current
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mb-3 min-h-[32px]">{tier.promise}</p>
                <div className="mb-3">
                  <span className="text-2xl font-bold">
                    ${interval === 'monthly' ? tier.monthly : Math.round(tier.annual / 12)}
                  </span>
                  <span className="text-xs text-muted-foreground ml-1">/mo</span>
                </div>
                <ul className="space-y-1">
                  {tier.highlights.map((h) => {
                    const checkColor = tier.slug === 'associate'
                      ? 'text-sky-500'
                      : tier.slug === 'partner'
                        ? 'text-brand-royal'
                        : 'text-violet-600 dark:text-violet-400'
                    return (
                      <li key={h} className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
                        <Check className={`h-3 w-3 mt-0.5 shrink-0 ${checkColor}`} strokeWidth={2.5} />
                        <span>{h}</span>
                      </li>
                    )
                  })}
                </ul>
              </button>
            )
          })}
        </div>

        {/* Preview panel */}
        {selectedTier && (
          <div className="rounded-xl border bg-muted/30 p-4">
            {previewLoading && (
              <div className="flex items-center text-muted-foreground text-sm">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Calculating proration…
              </div>
            )}
            {previewError && !previewLoading && (
              <div className="text-sm text-amber-600 dark:text-amber-400">{previewError}</div>
            )}
            {preview && !previewLoading && (
              <div className="space-y-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Due today (prorated)</span>
                  <span
                    className={`font-semibold tabular-nums ${
                      preview.immediateChargeCents < 0 ? 'text-emerald-600 dark:text-emerald-400' : ''
                    }`}
                  >
                    {preview.immediateChargeCents < 0
                      ? `${formatMoney(Math.abs(preview.immediateChargeCents), preview.currency)} credit`
                      : formatMoney(preview.immediateChargeCents, preview.currency)}
                  </span>
                </div>
                <div className="flex items-center justify-between border-t pt-2">
                  <span className="text-muted-foreground">Next invoice on {new Date(preview.currentPeriodEnd * 1000).toLocaleDateString()}</span>
                  <span className="font-semibold tabular-nums">
                    {formatMoney(preview.nextInvoiceTotalCents, preview.currency)}
                  </span>
                </div>
                <p className="text-[11px] text-muted-foreground pt-1">
                  {preview.immediateChargeCents < 0
                    ? 'Unused time from your current plan will be credited toward future invoices.'
                    : 'Charged immediately to your saved card. No further action needed.'}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={!selectedTier || submitting || previewLoading || !!previewError}
            className="flex-1 bg-gradient-to-r from-brand-royal to-brand-periwinkle hover:brightness-110 gap-2 text-white"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Updating plan…
              </>
            ) : (
              <>
                Confirm change
                <ArrowRight className="h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
